import { useEffect, useState } from 'react';

interface AccountPanesInput {
  isOpen: boolean;
  /** True while a save or delete is in flight. */
  saving: boolean;
  accountIds: string[];
}

export interface AccountPanes {
  /** Whether the user opened an item from the list; below `md` it decides which pane shows. */
  detailOpen: boolean;
  setDetailOpen: (open: boolean) => void;
  showOperationsAccount: boolean;
  setShowOperationsAccount: (show: boolean) => void;
  /** Call as the open account's delete is confirmed. */
  markDeleting: (accountId: string | null) => void;
}

/**
 * Which pane the AWS accounts dialog shows. The form hook auto-selects the first account, so its selection
 * cannot decide the pane below `md`; this state does, and it survives a rotation across `md`. Closing the
 * dialog resets it, so it reopens on the list, and a confirmed delete that removes the open account returns
 * to the list once the delete settles.
 */
export function useAccountPanes({ isOpen, saving, accountIds }: AccountPanesInput): AccountPanes {
  const [detailOpen, setDetailOpen] = useState(false);
  const [showOperationsAccount, setShowOperationsAccount] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [wasOpen, setWasOpen] = useState(isOpen);

  // Adjusted while rendering rather than in an effect, so a reopened dialog never draws the old detail.
  if (wasOpen !== isOpen) {
    setWasOpen(isOpen);
    if (!isOpen) {
      setDetailOpen(false);
      setShowOperationsAccount(false);
      setDeletingId(null);
    }
  }

  const deletedAccountGone = deletingId !== null && !accountIds.includes(deletingId);
  useEffect(() => {
    if (deletingId === null || saving) return;
    if (deletedAccountGone) setDetailOpen(false);
    setDeletingId(null);
  }, [deletingId, saving, deletedAccountGone]);

  return { detailOpen, setDetailOpen, showOperationsAccount, setShowOperationsAccount, markDeleting: setDeletingId };
}
